import React, { useEffect, useState } from "react";
import { Panel, Field } from "@/pages/admin/shared";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Save, ExternalLink, Bot, FileCode2 } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/pages/admin/api";

export default function SeoSettings() {
  const [data, setData] = useState(null);
  const [pages, setPages] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/seo").then((r) => setData(r.data));
    api.get("/seo/pages").then((r) => setPages(r.data));
  }, []);

  if (!data) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const set = (k, v) => setData({ ...data, [k]: v });
  const setPage = (path, k, v) => setPages(pages.map((p) => (p.path === path ? { ...p, [k]: v } : p)));

  const save = async () => {
    setSaving(true);
    try {
      const [r] = await Promise.all([api.put("/settings/seo", data), api.put("/seo/pages", pages)]);
      setData(r.data);
      toast.success("SEO settings updated");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const saveBtn = (
    <Button data-testid="save-seo" onClick={save} disabled={saving} className="rounded-xl bg-blue-600 hover:bg-blue-700 text-white">
      <Save size={14} className="mr-1" /> {saving ? "Saving..." : "Save"}
    </Button>
  );

  return (
    <div className="space-y-6">
      <Panel title="Site & Meta Tags" subtitle="Global defaults used when a page has no specific SEO." actions={saveBtn}>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Site URL" hint="Full domain, e.g. https://simpleinvoice.com — used in sitemap, canonical & schema">
            <Input data-testid="seo-site-url" value={data.site_url || ""} onChange={(e) => set("site_url", e.target.value)} placeholder="https://yourdomain.com" />
          </Field>
          <Field label="Site Title"><Input value={data.site_title || ""} onChange={(e) => set("site_title", e.target.value)} /></Field>
          <Field label="Default Meta Title"><Input value={data.meta_title || ""} onChange={(e) => set("meta_title", e.target.value)} /></Field>
          <Field label="Twitter Handle"><Input value={data.twitter_handle || ""} onChange={(e) => set("twitter_handle", e.target.value)} placeholder="@handle" /></Field>
          <Field label="Default Meta Description" className="md:col-span-2"><Textarea rows={2} value={data.meta_description || ""} onChange={(e) => set("meta_description", e.target.value)} /></Field>
          <Field label="Keywords" className="md:col-span-2" hint="Comma separated"><Input value={data.keywords || ""} onChange={(e) => set("keywords", e.target.value)} /></Field>
          <Field label="Default OG / Social Image URL"><Input value={data.default_og_image || data.og_image || ""} onChange={(e) => set("default_og_image", e.target.value)} /></Field>
          <Field label="Organization Name"><Input value={data.organization_name || ""} onChange={(e) => set("organization_name", e.target.value)} /></Field>
          <Field label="Organization Logo URL" className="md:col-span-2"><Input value={data.organization_logo || ""} onChange={(e) => set("organization_logo", e.target.value)} /></Field>
        </div>
      </Panel>

      <Panel title="AI Search Engine Optimisation" subtitle="Make the site discoverable and quotable by ChatGPT, Claude, Perplexity, Gemini and AI Overviews." actions={saveBtn}>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="flex items-center justify-between rounded-xl border border-border p-4">
            <div>
              <div className="text-sm font-medium flex items-center gap-2"><Bot size={14} /> Allow AI crawlers</div>
              <p className="text-xs text-muted-foreground">GPTBot, ClaudeBot, PerplexityBot, Google-Extended, CCBot & more.</p>
            </div>
            <Switch data-testid="seo-ai-crawlers-toggle" checked={!!data.allow_ai_crawlers} onCheckedChange={(v) => set("allow_ai_crawlers", v)} />
          </div>
          <div className="flex items-center justify-between rounded-xl border border-border p-4">
            <div>
              <div className="text-sm font-medium">Auto-generate llms.txt</div>
              <p className="text-xs text-muted-foreground">Builds an AI-readable site summary from your CMS content.</p>
            </div>
            <Switch data-testid="seo-auto-llms-toggle" checked={!!data.auto_llms} onCheckedChange={(v) => set("auto_llms", v)} />
          </div>
          <div className="flex items-center justify-between rounded-xl border border-border p-4">
            <div>
              <div className="text-sm font-medium">Structured data (JSON-LD)</div>
              <p className="text-xs text-muted-foreground">Organization, WebSite, WebApplication schema.</p>
            </div>
            <Switch data-testid="seo-schema-toggle" checked={data.enable_schema !== false} onCheckedChange={(v) => set("enable_schema", v)} />
          </div>
          <div className="flex items-center justify-between rounded-xl border border-border p-4">
            <div>
              <div className="text-sm font-medium">FAQ rich results</div>
              <p className="text-xs text-muted-foreground">Publishes FAQPage schema from FAQ Management.</p>
            </div>
            <Switch data-testid="seo-faq-schema-toggle" checked={data.enable_faq_schema !== false} onCheckedChange={(v) => set("enable_faq_schema", v)} />
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mt-4">
          {[["robots.txt", "/seo/robots.txt"], ["sitemap.xml", "/seo/sitemap.xml"], ["llms.txt", "/seo/llms.txt"], ["AI prerender", "/seo/prerender?path=/"]].map(([label, url]) => (
            <a key={label} data-testid={`view-${label}`} href={`${API}${url}`} target="_blank" rel="noreferrer">
              <Button variant="outline" size="sm" className="rounded-xl"><ExternalLink size={13} className="mr-1" /> {label}</Button>
            </a>
          ))}
        </div>
      </Panel>

      <Panel title="Per-Page SEO" subtitle="Title, description and indexing rules for each page." actions={saveBtn}>
        <div className="space-y-4">
          {pages.map((p) => (
            <div key={p.path} className="rounded-xl border border-border p-4" data-testid={`page-seo-${p.path === "/" ? "home" : p.path.replace("/", "")}`}>
              <div className="flex items-center justify-between mb-3 gap-3">
                <div>
                  <div className="text-sm font-medium">{p.name || p.path}</div>
                  <p className="text-xs text-muted-foreground font-mono">{p.path}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-xs text-muted-foreground">noindex</span>
                  <Switch checked={!!p.noindex} onCheckedChange={(v) => setPage(p.path, "noindex", v)} />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <Field label="Title" hint={`${(p.title || "").length}/60 characters`}>
                  <Input value={p.title || ""} onChange={(e) => setPage(p.path, "title", e.target.value)} />
                </Field>
                <Field label="Keywords"><Input value={p.keywords || ""} onChange={(e) => setPage(p.path, "keywords", e.target.value)} /></Field>
                <Field label="Meta Description" className="md:col-span-2" hint={`${(p.description || "").length}/160 characters`}>
                  <Textarea rows={2} value={p.description || ""} onChange={(e) => setPage(p.path, "description", e.target.value)} />
                </Field>
                <Field label="Canonical URL (optional)"><Input value={p.canonical || ""} onChange={(e) => setPage(p.path, "canonical", e.target.value)} /></Field>
                <Field label="OG Image URL (optional)"><Input value={p.og_image || ""} onChange={(e) => setPage(p.path, "og_image", e.target.value)} /></Field>
              </div>
            </div>
          ))}
        </div>
      </Panel>

      <Panel title="Advanced: Schema, robots.txt & llms.txt" actions={saveBtn}>
        <Field label="Extra JSON-LD" hint="Valid schema.org JSON (object or array) — merged with the auto-generated graph.">
          <Textarea data-testid="seo-schema-json" rows={5} className="font-mono text-xs" value={data.schema_json || ""} onChange={(e) => set("schema_json", e.target.value)} />
        </Field>
        <div className="flex items-center justify-between rounded-xl border border-border p-4 mt-4">
          <div>
            <div className="text-sm font-medium flex items-center gap-2"><FileCode2 size={14} /> Auto-generate robots.txt</div>
            <p className="text-xs text-muted-foreground">Turn off to serve your custom robots.txt below.</p>
          </div>
          <Switch data-testid="seo-auto-robots-toggle" checked={data.auto_robots !== false} onCheckedChange={(v) => set("auto_robots", v)} />
        </div>
        <Field label="Custom robots.txt" className="mt-4" hint="Used only when auto-generate is off.">
          <Textarea data-testid="seo-robots-txt" rows={6} className="font-mono text-xs" value={data.robots_txt || ""} onChange={(e) => set("robots_txt", e.target.value)} />
        </Field>
        <Field label="Custom llms.txt" className="mt-4" hint="Used only when auto-generate llms.txt is off.">
          <Textarea data-testid="seo-llms-txt" rows={6} className="font-mono text-xs" value={data.llms_txt || ""} onChange={(e) => set("llms_txt", e.target.value)} />
        </Field>
      </Panel>
    </div>
  );
}
